import { DEFAULT_OWNER_ONBOARDING_FIRST_NAME, DEFAULT_OWNER_ONBOARDING_LAST_NAME } from '../constants/user.constants';
import { NameRepresentation, NameSortation } from '../enum/user.setting.enum';
import { normalizeWhitespace } from './string.utils';

export type SplitFullNameResult = {
  firstName?: string;
  lastName?: string;
};

export type IGenericPerson = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

function getOrderedNameParts(person: IGenericPerson, representation: NameRepresentation): string[] {
  const firstName = person.firstName?.trim() ?? '';
  const lastName = person.lastName?.trim() ?? '';
  const parts =
    representation === NameRepresentation.firstNameBeforeLastName ? [firstName, lastName] : [lastName, firstName];
  return parts.filter(Boolean);
}

export function getUserFullName(user: IGenericPerson, representation: NameRepresentation): string {
  return getOrderedNameParts(user, representation).join(' ');
}

export function getStudentFullName(student: IGenericPerson, representation: NameRepresentation): string {
  return getUserFullName(student, representation);
}

export function getUserFullNameOrEmail(user: IGenericPerson, representation: NameRepresentation): string {
  return getUserFullName(user, representation) || (user.email ?? '');
}

export function getUserInitials(user: IGenericPerson, representation: NameRepresentation): string {
  return getOrderedNameParts(user, representation)
    .map((part) => part.charAt(0))
    .join('');
}

export function isPlaceholderName(user?: IGenericPerson | null): boolean {
  return (
    user?.firstName?.trim() === DEFAULT_OWNER_ONBOARDING_FIRST_NAME &&
    user.lastName?.trim() === DEFAULT_OWNER_ONBOARDING_LAST_NAME
  );
}

export function getUserSalutationName(user?: IGenericPerson | null): string {
  const firstName = user?.firstName?.trim() ?? '';
  const fullName = getUserFullName(user ?? {}, NameRepresentation.firstNameBeforeLastName);

  if (!fullName) return '';
  if (isPlaceholderName(user)) return fullName;
  if (firstName) return firstName;
  return fullName;
}

export function getNameSortValue(person: IGenericPerson, sortation: NameSortation): string {
  const representation =
    sortation === NameSortation.sortByFirstName
      ? NameRepresentation.firstNameBeforeLastName
      : NameRepresentation.lastNameBeforeFirstName;
  return getUserFullName(person, representation);
}

export function splitFullName(fullName: string | null | undefined): SplitFullNameResult {
  const normalizedFullName = fullName ? normalizeWhitespace(fullName) : '';

  if (!normalizedFullName) {
    return {};
  }

  const lastSpaceIndex = normalizedFullName.lastIndexOf(' ');

  if (lastSpaceIndex === -1) {
    return { firstName: normalizedFullName };
  }

  return {
    firstName: normalizedFullName.slice(0, lastSpaceIndex),
    lastName: normalizedFullName.slice(lastSpaceIndex + 1),
  };
}
